<template>
	<v-dialog :model-value="assignDutyStore.isModalOpen" max-width="620" persistent @update:model-value="assignDutyStore.closeModal">
		<v-card class="assign-duty-modal" elevation="4" rounded="lg">
			<div class="modal-header">
				<div class="header-left">
					<span class="modal-title">{{ t('planning_duty_assign') }}</span>
					<v-chip v-if="assignDutyStore.targetDate" class="date-chip" size="small" variant="tonal" color="grey-darken-1" rounded="md">
						<v-icon size="13" class="mr-1">mdi-calendar</v-icon>
						{{ formattedDate }}
					</v-chip>
				</div>
				<v-btn icon variant="text" size="small" @click="assignDutyStore.closeModal">
					<v-icon size="20">mdi-close</v-icon>
				</v-btn>
			</div>

			<v-divider />

			<v-card-text class="modal-body">
				<div class="toggle-row">
					<v-switch
						v-model="form.driverFixed"
						:disabled="!hasDriverSelected"
						:color="!hasDriverSelected ? 'grey-lighten-1' : 'primary-green'"
						density="compact"
						hide-details
						flat
					/>
					<span class="toggle-label">{{ t('planning_driver_fixed_on_duty') }}</span>
				</div>

				<v-row class="mt-5" no-gutters>
					<v-col cols="5" class="pr-4">
						<div class="field-label">{{ t('planning_driver') }}</div>

						<div v-if="assignDutyStore.isDriverLocked" class="locked-driver">
							<span class="locked-driver-name">{{ assignDutyStore.preselectedDriverName }}</span>
							<v-icon size="14" color="grey-lighten-1">mdi-lock-outline</v-icon>
						</div>

						<v-autocomplete
							v-else
							v-model="form.driverId"
							:items="driverOptions"
							item-title="label"
							item-value="value"
							:placeholder="t('select_driver')"
							variant="underlined"
							density="compact"
							hide-details
							color="primary-green"
							auto-select-first
						/>
					</v-col>

					<v-col cols="7" class="pl-3">
						<div class="field-label mt-1">{{ t('user_field_type') }}</div>
						<v-select
							v-model="form.type"
							:items="typeOptions"
							item-title="label"
							item-value="value"
							variant="underlined"
							density="compact"
							hide-details
							color="primary-green"
							:menu-props="{ contentClass: '' }"
						>
							<template #selection="{ item }">
								<span>
									{{ item.title }}
								</span>
							</template>
						</v-select>
					</v-col>
				</v-row>

				<v-row class="mt-5" no-gutters align="center">
					<v-col cols="11">
						<div class="field-label">{{ t('planning_disposition') }}</div>
						<DropDownWithChips
							v-model="form.disposition"
							:items="assignDutyStore.availableDispositions || {}"
							label-key="name"
							search-placeholder="duty"
							:disabled="assignDutyStore.dispositionsLoading || !isWorkDay"
						/>
					</v-col>
					<v-col cols="1" class="d-flex justify-end align-end pb-1 pt-10">
						<v-btn icon variant="text" size="small" color="primary-green" class="add-btn" @click="onCreateDuty">
							<v-icon size="20">mdi-plus</v-icon>
						</v-btn>
					</v-col>
				</v-row>
			</v-card-text>

			<v-divider />

			<v-card-actions class="modal-actions">
				<v-btn variant="text" class="cancel-btn" @click="assignDutyStore.closeModal"> {{ t('action_cancel') }} </v-btn>
				<v-spacer />
				<v-btn
					v-if="!isPastOrPublishedDay || !isActive"
					variant="text"
					color="primary-green"
					class="save-btn font-weight-bold"
					:disabled="!isFormValid"
					:loading="isSaving"
					@click="handleSave"
				>
					{{ t('action_save') }}
				</v-btn>
				<v-btn
					v-if="isPastOrPublishedDay"
					variant="flat"
					color="teal-darken-4"
					:disabled="!isFormValid"
					:loading="previewButtonLoading"
					@click="() => handlePreviewButtonClick(() => handleSave())"
				>
					<v-progress-circular v-if="previewButtonLoading" indeterminate size="18" width="2" />
					<span v-else>{{ $t(previewButtonLabelKey) }}</span>
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>

	<PlanningCreateDutyDialog v-if="createDutyStore.isOpen" />
</template>

<script setup lang="ts">
import { usePlanningCreateDutyStore } from '@/rennovation/planning-view/store/usePlanningCreateDutyStore';
import { useAssignDutyForm } from '@/rennovation/planning-view/composables/useAssignDutyForm';
import DropDownWithChips from '@/rennovation/planning-view/components/Controls/DropDownWithChips.vue';
import { useDutyChangePreview } from '@/rennovation/planning-view/composables/useDutyChangePreview';
import { useDayStore } from '@/rennovation/planning-view/store/useDayStore';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import { PLANNING_DAY_STATUS } from '@/rennovation/planning-view/constants';
import PlanningCreateDutyDialog from '@/rennovation/planning-view/components/Layout/Modals/PlanningCreateDutyDialog.vue';

const createDutyStore = usePlanningCreateDutyStore();
const { form, isSaving, isWorkDay, isFormValid, typeOptions, driverOptions, formattedDate, assignDutyStore, hasDriverSelected, handleSave } =
	useAssignDutyForm();
const { isActive, previewButtonLoading, previewButtonLabelKey, handlePreviewButtonClick } = useDutyChangePreview();
const { t } = useI18n();
const dayStore = useDayStore();
const isPastOrPublishedDay = computed(() => dayStore.isPastOrPublishedDay(assignDutyStore.targetDate));

const onCreateDuty = () => {
	let targetDriverName = '';
	let targetDriverId = '';

	if (assignDutyStore.isDriverLocked) {
		targetDriverName = assignDutyStore.preselectedDriverName;
		targetDriverId = assignDutyStore.preselectedDriverId;
	}
	const targetDate = assignDutyStore.targetDate;
	createDutyStore.openWithContext({
		driverName: targetDriverName,
		driverId: targetDriverId,
		date: targetDate,
	});
};
</script>

<style lang="scss" scoped>
.assign-duty-modal {
	font-family: Roboto, sans-serif;
}

.modal-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 16px 20px 12px;
}

.header-left {
	display: flex;
	align-items: center;
	gap: 12px;
}

.modal-title {
	font-size: 15px;
	font-weight: 600;
	color: #1a1a1a;
	letter-spacing: 0.01em;
}

.date-chip {
	font-size: 12px;
	font-weight: 500;
}

.modal-body {
	padding: 20px 24px 16px !important;
}

.toggle-row {
	display: flex;
	align-items: center;
	gap: 8px;
}

.toggle-label {
	font-size: 13px;
	color: #444;
}

.field-label {
	font-size: 11px;
	font-weight: 500;
	color: #888;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	margin-bottom: 2px;
}

.locked-driver {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 6px 0 7px;
	border-bottom: 1px solid rgba(0, 0, 0, 0.12);
	gap: 8px;
}

.locked-driver-name {
	font-size: 14px;
	font-weight: 500;
	color: #333;
}

.modal-actions {
	padding: 10px 16px 12px;
}

.cancel-btn {
	font-size: 13px;
	color: #666;
	letter-spacing: 0.02em;
}

.save-btn {
	font-size: 13px;
	letter-spacing: 0.04em;
}

.add-btn {
	opacity: 0.7;
	transition: opacity 0.15s;

	&:hover {
		opacity: 1;
	}
}
</style>
